export type PoolSettlement<TResult> =
  | { index: number; status: "fulfilled"; value: TResult }
  | { index: number; status: "rejected"; reason: unknown };

type RunConcurrentPoolOptions<TItem, TResult> = {
  concurrency: number;
  items: readonly TItem[];
  onSettled?: (settlement: PoolSettlement<TResult>) => void;
  run: (item: TItem, index: number) => Promise<TResult>;
  signal?: AbortSignal;
};

/** Runs `run` over `items` with at most `concurrency` in flight.
 *
 * Never rejects: a failing item is reported through onSettled as "rejected"
 * and the remaining items carry on. That is what stops one bad chunk sinking
 * a whole upload.
 *
 * Aborting stops new items being picked up; items already in flight run to
 * completion (their own request carries the same signal). */
export const runConcurrentPool = async <TItem, TResult>({
  concurrency,
  items,
  onSettled,
  run,
  signal,
}: RunConcurrentPoolOptions<TItem, TResult>): Promise<void> => {
  let cursor = 0;

  const worker = async (): Promise<void> => {
    for (;;) {
      // Safe despite looking racy: there is no await between reading and
      // incrementing, so no two workers can claim the same index.
      const index = cursor;
      cursor += 1;

      if (index >= items.length || signal?.aborted) {
        return;
      }

      try {
        const value = await run(items[index], index);
        onSettled?.({ index, status: "fulfilled", value });
      } catch (reason) {
        onSettled?.({ index, status: "rejected", reason });
      }
    }
  };

  const workerCount = Math.max(Math.min(concurrency, items.length), 0);

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
};
